import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { AuthResponse } from "../../typings/AuthResponse";
import { SliceStatus } from "../utils";
import authApi from "./authApi";
import { setBearerToken } from "../../utils/http";

interface AuthState {
  user: AuthResponse["user"] | null;
  token: string | null;
  status: SliceStatus;
  error: string | null;
}

// Safely parse user from localStorage
const storedToken = localStorage.getItem("token");
const storedUser = localStorage.getItem("user");

// Ensure storedUser is valid before parsing
const parsedUser = storedUser && storedUser !== "undefined" ? JSON.parse(storedUser) : null;

const initialState: AuthState = {
  user: parsedUser,
  token: storedToken || null,
  status: SliceStatus.IDLE,
  error: null,
};

// Login Thunk
export const login = createAsyncThunk(
  "auth/login",
  async (payload: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.login(payload);
      localStorage.setItem("token", response.tokens.access);
      localStorage.setItem("user", response.user ? JSON.stringify(response.user) : "null");
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Invalid credentials");
    }
  }
);

// Register Thunk
export const register = createAsyncThunk(
  "auth/register",
  async (
    payload: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      confirmPassword: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await authApi.register(payload);
      localStorage.setItem("token", response.tokens.access);
      localStorage.setItem("user", response.user ? JSON.stringify(response.user) : "null");
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Registration failed");
    }
  }
);

export const deactivateAccount = createAsyncThunk(
  "auth/deactivateAccount",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await authApi.deactivateAccount(userId);

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return response.detail;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || "Deactivation failed");
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.status = SliceStatus.IDLE;
      state.error = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setBearerToken(null)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.status = SliceStatus.LOADING;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, { payload }) => {
      state.user = payload.user;
      state.token = payload.tokens.access;
      state.status = SliceStatus.IDLE;
    });
    builder.addCase(login.rejected, (state, { payload }) => {
      state.status = SliceStatus.FAILED;
      state.error = payload as string;
    });

    builder.addCase(register.pending, (state) => {
      state.status = SliceStatus.LOADING;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state, { payload }) => {
      state.user = payload.user;
      state.token = payload.tokens.access;
      state.status = SliceStatus.IDLE;
    });
    builder.addCase(register.rejected, (state, { payload }) => {
      state.status = SliceStatus.FAILED;
      state.error = payload as string;
    });

    builder.addCase(deactivateAccount.pending, (state) => {
      state.status = SliceStatus.LOADING;
      state.error = null;
    });
    builder.addCase(deactivateAccount.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.status = SliceStatus.IDLE;
      state.error = null;
    });
    builder.addCase(deactivateAccount.rejected, (state, { payload }) => {
      state.status = SliceStatus.FAILED;
      state.error = payload as string;
    });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
